import React, { useState } from 'react';
import { Mail, Lock, ArrowRight, Sun, Moon } from 'lucide-react';
import axiosClient from '../api/axiosClient';
import Research_rex from '../assets/regrexnew.png';

const Login = ({ isDark, setIsDark, onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Slate 300 for dark and Slate 600 for light per your instructions
  const slateText = isDark ? 'text-slate-300' : 'text-slate-600';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axiosClient.post('/login.php', { email, password });
      
      if (response.data.status === 'success') {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        onLogin(response.data.user);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Server Connection Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex transition-all duration-500 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
      
      {/* LEFT SIDE: Pure Background Image only */}
      <div className="hidden lg:flex w-[45%] relative overflow-hidden">
        <img 
          src={Research_rex} 
          alt="Login Background"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />

        {/* STRAIGHT LINE DIVIDER - To maintain the transition to the form */}
        <div 
          className="absolute top-0 right-[-1px] h-full w-[100px] z-30 pointer-events-none"
          style={{
            background: isDark ? "#0f172a" : "#f8fafc",
            clipPath: "polygon(100% 0, 0% 0, 100% 100%)"
          }}
        ></div>
      </div>

      {/* RIGHT SIDE: Form */}
      <div className="flex-1 flex flex-col relative">
        <button 
          onClick={() => setIsDark(!isDark)}
          className={`absolute top-8 right-8 p-3 rounded-2xl border transition-all z-50 cursor-pointer ${
            isDark ? 'bg-slate-800 border-slate-700 text-yellow-400' : 'bg-white border-slate-200 text-slate-400'
          }`}
        >
          {isDark ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md">
            <div className="mb-12 text-center lg:text-left">
              <h2 className={`text-4xl font-black uppercase tracking-widest leading-none ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Sign In
              </h2>
              <p className={`text-[11px] font-bold uppercase tracking-[0.3em] mt-4 ${slateText}`}>
                Internal Access Only
              </p>
            </div>

            {error && (
              <div className="mb-8 p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-500 text-[10px] font-black uppercase tracking-widest text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className={`text-[10px] font-black uppercase tracking-widest ml-1 ${slateText}`}>Email</label>
                <div className="relative group">
                  <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500 transition-colors" size={18} />
                  <input 
                    type="email" 
                    required
                    placeholder="name@gmail.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full h-16 pl-14 rounded-2xl border-2 outline-none transition-all font-bold text-sm ${
                      isDark 
                      ? 'bg-slate-800 border-slate-700 text-white focus:border-white focus:bg-slate-950' 
                      : 'bg-white border-slate-100 text-slate-900 focus:border-slate-900'
                    }`}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className={`text-[10px] font-black uppercase tracking-widest ml-1 ${slateText}`}>Password</label>
                <div className="relative group">
                  <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500 transition-colors" size={18} />
                  <input 
                    type="password" 
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full h-16 pl-14 rounded-2xl border-2 outline-none transition-all font-bold text-sm ${
                      isDark 
                      ? 'bg-slate-800 border-slate-700 text-white focus:border-white focus:bg-slate-950' 
                      : 'bg-white border-slate-100 text-slate-900 focus:border-slate-900'
                    }`}
                  />
                </div>
              </div>

              <button 
                type="submit"
                disabled={loading}
                className={`w-full h-16 rounded-2xl font-black uppercase tracking-[0.25em] text-[11px] transition-all flex items-center justify-center gap-3 active:scale-[0.98] disabled:opacity-50 mt-4 cursor-pointer
                  ${isDark 
                    ? 'bg-white text-black hover:bg-slate-200' 
                    : 'bg-slate-900 text-white hover:bg-black shadow-2xl shadow-slate-900/20'
                  }`}
              >
                {loading ? "Verifying..." : "Enter Portal"} <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;